import React from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    Paper,
    useTheme,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import AdBanner from '../components/AdBanner';
import SubscriptionPlan from "../components/SubscriptionPlan";

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const {isAuthenticated} = useAuth();
    const theme = useTheme();

    const features = [
        {
            title: 'Gestione Sicura',
            description: 'I tuoi dati personali sono protetti con crittografia avanzata e conformità GDPR.',
            icon: '🔒',
        },
        {
            title: 'Versioni Multiple',
            description: 'Crea diverse versioni del tuo CV per diverse opportunità di lavoro.',
            icon: '📄',
        },
        {
            title: 'Template Professionali',
            description: 'Scegli tra template moderni e professionali per il tuo CV.',
            icon: '🎨',
        },
        {
            title: 'Condivisione Protetta',
            description: 'Condividi il tuo CV con link protetti e password opzionali.',
            icon: '🔗',
        },
        {
            title: 'Export PDF',
            description: 'Genera PDF di alta qualità per le tue candidature.',
            icon: '📋',
        },
        {
            title: 'Sincronizzazione',
            description: 'I tuoi dati sono sempre sincronizzati e accessibili ovunque.',
            icon: '☁️',
        },
    ];

    return (
        <Box>
            {/* Hero Section */}
            <Paper
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    color: 'white',
                    py: 8,
                    mb: 4,
                }}
            >
                <Container maxWidth="lg">
                    <Box textAlign="center">
                        <Typography variant="h2" component="h1" gutterBottom>
                            CV Portal
                        </Typography>
                        <Typography variant="h5" component="h2" gutterBottom sx={{mb: 4}}>
                            La piattaforma sicura per gestire e condividere il tuo curriculum vitae
                        </Typography>
                        <Box sx={{display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap'}}>
                            {isAuthenticated ? (
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/dashboard')}
                                    sx={{bgcolor: 'white', color: 'primary.main', '&:hover': {bgcolor: 'grey.100'}}}
                                >
                                    Vai alla Dashboard
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate('/register')}
                                        sx={{bgcolor: 'white', color: 'primary.main', '&:hover': {bgcolor: 'grey.100'}}}
                                    >
                                        Inizia Gratis
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={() => navigate('/login')}
                                        sx={{
                                            borderColor: 'white',
                                            color: 'white',
                                            '&:hover': {borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)'}
                                        }}
                                    >
                                        Accedi
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>
                </Container>
            </Paper>

            <Container maxWidth="lg">
                {/* Features Section */}
                <Box sx={{mb: 8}}>
                    <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
                        Perché scegliere CV Portal?
                    </Typography>
                    <Typography variant="h6" textAlign="center" color="text.secondary" sx={{mb: 6}}>
                        Una soluzione completa per la gestione professionale del tuo curriculum
                    </Typography>

                    <Grid container spacing={4}>
                        {features.map((feature, index) => (
                            <Grid size={{xs: 12, md: 6, lg: 4}} key={index}>
                                <Card sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                    <CardContent sx={{flexGrow: 1}}>
                                        <Typography variant="h4" component="div" sx={{mb: 2}}>
                                            {feature.icon}
                                        </Typography>
                                        <Typography variant="h6" component="h3" gutterBottom>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {feature.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Pricing Section */}
                <SubscriptionPlan/>

                {/* CTA Section */}
                <Box sx={{textAlign: 'center', py: 6}}>
                    <Typography variant="h4" component="h2" gutterBottom>
                        Pronto a iniziare?
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{mb: 4}}>
                        Crea il tuo account gratuito e inizia a costruire il tuo CV professionale
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/register')}
                    >
                        Registrati Ora
                    </Button>
                </Box>
            </Container>

            {/* Ad Banner for non-premium users */}
            <AdBanner/>
        </Box>
    );
};

export default HomePage;
